import { Role, type PrincipalResponse } from '../api';

/**
 * The two halves of the product, and who belongs in which.
 *
 * vyttech staff work in the back-office (#216); the people at a customer
 * company work in the portal (#214). They are not two views of one screen set:
 * a customer HR user must not be able to reach the back-office at all, and a
 * staff user has no business inside a single company's portal. The mapping is
 * stated once here and enforced once in the router, so a screen added later
 * cannot land in the wrong half by accident.
 *
 * The role comes from `GET /auth/me` -- the server's answer about who the
 * caller is -- never from decoding a token in the browser. This routing is a
 * matter of showing people the right thing; the actual protection is the
 * backend refusing the request, which it does regardless of what the browser
 * believes.
 */
export type Area = 'staff' | 'customer';

const AREA_BY_ROLE: Record<Role, Area | null> = {
  [Role.VYTTECH_ADMIN]: 'staff',
  [Role.VYTTECH_ANALYST]: 'staff',
  [Role.CUSTOMER_ADMIN]: 'customer',
  [Role.CUSTOMER_USER]: 'customer',
  // Machine credentials have no person behind them and no web session. They
  // reach the API directly; there is nothing here for them.
  [Role.MACHINE_READ]: null,
  [Role.MACHINE_WRITE]: null,
};

export function areaForRole(role: Role): Area | null {
  return AREA_BY_ROLE[role] ?? null;
}

export function areaForPrincipal(principal: PrincipalResponse): Area | null {
  return areaForRole(principal.role);
}

export const AREA_LABEL: Record<Area, string> = {
  staff: 'Back-office vyttech',
  customer: 'Portal do cliente',
};
